import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import BaseDialog from "@/components/Modals/BaseDialog";
import { Button } from "@/components/shadcn/button";
import { Input } from "@/components/shadcn/input";
import {
  AlertTriangle,
  Check,
  Circle,
  StopCircle,
  Play,
  MousePointer,
  Trash2,
  Loader2,
} from "lucide-react";
import { useSetAtom } from "jotai";
import { useSpotUser } from "@/utils/spotUser";
import { isRecordStudioOpenAtom } from "@/store/editor-jotai";
import { buildKeyCommand, isClipboardShortcut } from "./recordKeyMap";
import { useUpstreamVariableOptions } from "@/components/SpotflowV2/Sidebar/InlineVariablePicker";
import { buildOptionsByValue } from "@/components/SpotflowV2/Sidebar/variableOptions";
import {
  VAR_REF_PATTERN,
  WHOLE_VAR_REF_PATTERN,
  extractVarRefIdsFromString,
  normalizeVariableRefId,
} from "@/components/SpotflowV2/Sidebar/variableRefs";

// ---------- Types mirroring the backend event payloads ----------
//
// Frames arrive as binary WebSocket messages (see `_encode_frame_message`
// in spotflow_backend.api.playbook). Everything else is JSON.

type FrameMeta = {
  device_width: number;
  device_height: number;
  page_scale: number;
  offset_top: number;
  scroll_x: number;
  scroll_y: number;
};

type StepEvent = { type: "step"; step: any; index: number };
type StepUpdateEvent = { type: "step_update"; step: any; index: number };
type PickEvent = {
  type: "pick";
  request_id: string;
  selector: any | null;
  bbox: { x: number; y: number; width: number; height: number } | null;
};
type StatusEvent = {
  type: "status";
  recording: boolean;
  url: string;
  title: string;
};
type BBoxEvent = {
  type: "bbox";
  request_id: string;
  bbox: { x: number; y: number; width: number; height: number } | null;
};
type ReplayEvent = {
  type: "replay";
  request_id: string;
  success: boolean;
  error: string | null;
};
type ErrorEvent = { type: "error"; message: string; code?: string | null };
type QueuedEvent = {
  type: "queued";
  queue_position: number | null;
  queue_depth: number | null;
  wait_seconds: number;
};
type ClipboardDataEvent = {
  type: "clipboard_data";
  text: string | null;
  error: string | null;
};
type SessionCreatedEvent = {
  type: "session_created";
  session_id: string;
  environment: RecordingEnvironment;
};

type SessionEvent =
  | StepEvent
  | StepUpdateEvent
  | StatusEvent
  | BBoxEvent
  | PickEvent
  | ReplayEvent
  | ErrorEvent
  | QueuedEvent
  | ClipboardDataEvent
  | SessionCreatedEvent;

// Binary WS message type prefix (mirrors `_WS_MSG_FRAME` on the backend).
// v2 = fixed 25-byte header (u8 type + 6 big-endian IEEE-754 f32s).
const WS_MSG_FRAME = 0x02;
const FRAME_HEADER_BYTES = 25; // 1 type + 6 * 4 bytes

function parseFrameMessage(
  buf: ArrayBuffer
): { meta: FrameMeta; jpeg: Blob } | null {
  if (buf.byteLength < FRAME_HEADER_BYTES) {
    console.warn("[recording] frame too short", buf.byteLength);
    return null;
  }
  const view = new DataView(buf);
  const type = view.getUint8(0);
  if (type !== WS_MSG_FRAME) {
    console.warn("[recording] unknown frame type byte", type);
    return null;
  }
  // Six f32s, packed big-endian in order: device_width, device_height,
  // page_scale, offset_top, scroll_x, scroll_y.  Matches
  // ``_FRAME_META_STRUCT`` in spotflow_backend.api.playbook.
  const meta: FrameMeta = {
    device_width: view.getFloat32(1, false),
    device_height: view.getFloat32(5, false),
    page_scale: view.getFloat32(9, false),
    offset_top: view.getFloat32(13, false),
    scroll_x: view.getFloat32(17, false),
    scroll_y: view.getFloat32(21, false),
  };
  // Slice copies the bytes into an independent ArrayBuffer — Blob owns
  // the copy and the original buf can be GC'd without invalidating it.
  const jpegBuf = buf.slice(FRAME_HEADER_BYTES);
  const jpeg = new Blob([jpegBuf], { type: "image/jpeg" });
  return { meta, jpeg };
}

// ---------- Connection-quality thresholds ----------
//
// Measured against median command round-trip time. "good" is snappy local
// feel; "degraded" means the cloud browser is struggling and we reduce
// screencast quality to keep the UI responsive.

type QualityLevel = "good" | "laggy" | "degraded";

function qualityForRtt(rttMs: number): QualityLevel {
  if (rttMs < 200) return "good";
  if (rttMs < 600) return "laggy";
  return "degraded";
}

// Screencast params dispatched to the backend per quality level.
// (See set_screencast_params in recording.py.)
//
// Adapt JPEG quality, not the browser viewport. Keep every frame so a final
// keystroke or click is still visible when the page stops repainting.
const SCREENCAST_PRESETS: Record<
  QualityLevel,
  { quality: number; every_nth_frame: number }
> = {
  good: { quality: 45, every_nth_frame: 1 },
  laggy: { quality: 35, every_nth_frame: 1 },
  degraded: { quality: 25, every_nth_frame: 1 },
};

// ---------- Props ----------

// Browser environment config inherited from the Browser Playbook node so
// captured playbooks run in the same environment as execution.
export interface BrowserConfig {
  stealth_mode: boolean;
  use_proxy: boolean;
  proxy_region: string;
  use_cloud_browser: boolean;
  viewport_width: number;
  viewport_height: number;
  // ON_PREM-gated on the Browser Playbook node. When true the recorder
  // skips the mitmproxy + stealth flags so it can drive enterprise SSO
  // endpoints. The backend mirrors this in PlaybookExecutor at execution
  // time so what you record is what runs.
  disable_local_protection?: boolean;
}

interface RecordingEnvironment {
  cdp: boolean;
  proxy: boolean;
  stealth: boolean;
}

interface RecordModeModalProps {
  open: boolean;
  onClose: () => void;
  initialSteps: any[];
  onSave: (steps: any[]) => void;
  /** Browser environment to use for the recording session.  When omitted,
   * the backend uses defaults matching the v4 node's defaults. */
  browserConfig?: BrowserConfig;
}

// ---------- Helpers ----------

function buildWsUrl(groupId: string, wsToken: string): string {
  // Same-origin through the Next.js custom server (server.js), which
  // authenticates the upgrade and forwards it to BACKEND_URL. Going
  // direct would need a browser-reachable backend and would break on
  // HTTPS (ws:// from an https page).
  const wsScheme = window.location.protocol === "https:" ? "wss" : "ws";
  const params = new URLSearchParams({ token: wsToken });
  return `${wsScheme}://${window.location.host}/api/backend/groups/${groupId}/playbook/record/ws?${params.toString()}`;
}

/** Whether any value in the step carries a `:var[...]` ref. Bound variables
 * only have values during a workflow run, so such steps can't be replayed
 * against the live recording browser. */
function stepHasVarRefs(step: any): boolean {
  try {
    return extractVarRefIdsFromString(JSON.stringify(step)).length > 0;
  } catch {
    return false;
  }
}

function stepNotReplayable(step: any): boolean {
  return stepHasVarRefs(step) || step?.type === "run_script";
}

const SCRIPT_REPLAY_UNAVAILABLE_TITLE =
  "Replay isn't available for script steps; they run when the workflow runs.";

function replayUnavailableTitle(step: any): string {
  return step?.type === "run_script"
    ? SCRIPT_REPLAY_UNAVAILABLE_TITLE
    : REPLAY_UNAVAILABLE_TITLE;
}

const REPLAY_UNAVAILABLE_TITLE =
  "Replay isn't available. This step uses a bound variable whose value only exists when the workflow runs.";

const FIXED_LENGTH_MASK = "••••••••";

function isMaskedLiteral(step: { sensitive?: boolean; value?: unknown }) {
  return (
    !!step.sensitive &&
    typeof step.value === "string" &&
    step.value !== "" &&
    !WHOLE_VAR_REF_PATTERN.test(step.value)
  );
}

function stepLabel(
  step: any,
  formatValue: (text: string) => string = (text) => text
): string {
  const fmt = (v: unknown) =>
    typeof v === "string" ? formatValue(v) : `${v ?? ""}`;
  const t = step?.type;
  if (t === "navigate") return `Navigate → ${fmt(step.url)}`;
  if (t === "click")
    return `Click ${step.selector?.description || step.selector?.primary || "element"}`;
  if (t === "fill")
    return `Fill ${step.selector?.description || step.selector?.primary || "field"} = "${isMaskedLiteral(step) ? FIXED_LENGTH_MASK : fmt(step.value)}"`;
  if (t === "select") {
    const v = Array.isArray(step.value)
      ? step.value.map(fmt).join(", ")
      : fmt(step.value);
    return `Select ${step.selector?.description || step.selector?.primary || "dropdown"} = "${v}"`;
  }
  if (t === "type") return `Type "${fmt(step.text)}"`;
  if (t === "press") return `Press ${step.key}`;
  if (t === "wait_for") return `Wait for element`;
  if (t === "run_script")
    return `Run script → ${(step.outputs || []).join(", ") || "no outputs"}`;
  return t || "step";
}

// ---------- Component ----------

export function RecordModeModal({
  open,
  onClose,
  initialSteps,
  onSave,
  browserConfig,
}: RecordModeModalProps) {
  const { spotGroup } = useSpotUser();
  const groupId = spotGroup.id;

  // Step labels show the variable's label for `:var[...]` tokens; outside the
  // V2 editor the options are empty and values render as-is.
  const variableOptions = useUpstreamVariableOptions();
  const optionsByRef = useMemo(
    () => buildOptionsByValue(variableOptions),
    [variableOptions]
  );
  const formatStepValue = useCallback(
    (text: string) =>
      text.replace(VAR_REF_PATTERN, (match, ref: string) => {
        const entry = optionsByRef.get(normalizeVariableRefId(ref));
        return entry ? entry.output.outputLabel : match;
      }),
    [optionsByRef]
  );

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [steps, setSteps] = useState<any[]>(initialSteps || []);
  const [recording, setRecording] = useState(false);
  const [urlBar, setUrlBar] = useState("");
  const [status, setStatus] = useState<{ url: string; title: string }>({
    url: "",
    title: "",
  });
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [replayingIndex, setReplayingIndex] = useState<number | null>(null);
  const [replayedSteps, setReplayedSteps] = useState<Set<number>>(new Set());
  const [isWaitingForSlot, setIsWaitingForSlot] = useState(false);
  // Set while the browser's sandbox waits in the sandbox manager's queue.
  const [queue, setQueue] = useState<{
    position: number | null;
    depth: number | null;
  } | null>(null);
  const [environment, setEnvironment] = useState<RecordingEnvironment | null>(
    null
  );

  // Perceived-latency UX state. None of these touch the wire — they are
  // local-only feedback to mask the cloud round-trip.
  const [ripples, setRipples] = useState<
    { id: string; x: number; y: number }[]
  >([]);
  const [ghostCursor, setGhostCursor] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [navPending, setNavPending] = useState<string | null>(null);
  const [quality, setQuality] = useState<QualityLevel | null>(null);
  // First-frame gating — the <img> has no src until a frame arrives, so
  // we show an explicit placeholder rather than the browser's broken-
  // image chrome when the cloud browser is still spinning up.
  const [hasFirstFrame, setHasFirstFrame] = useState(false);
  // Ref-mirror so the WS onclose handler can read the latest value
  // without depending on a stale React closure.
  const hasFirstFrameRef = useRef(false);

  const wsRef = useRef<WebSocket | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const stepListRef = useRef<HTMLDivElement | null>(null);

  // Device (browser) dimensions for coordinate translation.
  const frameMetaRef = useRef<{
    device_width: number;
    device_height: number;
    scroll_x: number;
    scroll_y: number;
  }>({ device_width: 1280, device_height: 720, scroll_x: 0, scroll_y: 0 });

  const pendingBBoxResolversRef = useRef<
    Map<string, (bbox: BBoxEvent["bbox"]) => void>
  >(new Map());
  const pendingReplayResolversRef = useRef<
    Map<string, (r: { success: boolean; error: string | null }) => void>
  >(new Map());
  const latestPickRequestRef = useRef<string>("");
  // Pick-on-pause: pick_at is a full page.evaluate round-trip on the
  // remote browser; firing one per mousemove sample saturates the
  // transatlantic socket and queues clicks behind in-flight evals.  We
  // debounce: only fire when the pointer has been stationary for a
  // moment.  Sweeping across the viewport now costs zero round-trips.
  const pickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingPickPosRef = useRef<{ x: number; y: number } | null>(null);

  // Blob URL tracking — we render frames via URL.createObjectURL(blob) and
  // must revoke the previous URL on each frame (and on unmount) to avoid
  // leaking decoded JPEGs in the browser's internal map.
  const lastFrameUrlRef = useRef<string | null>(null);

  // Connection-quality sampling.
  //
  // - `pickSendTsRef`: send timestamp of the most recent pick_at, matched
  //   by request_id on the pick reply to yield one RTT sample.
  // - `rttSamplesRef`: rolling window (last 10 samples) used to derive
  //   the current QualityLevel.
  // - `currentQualityRef`: last-dispatched level; used to debounce
  //   set_quality messages.
  const pickSendTsRef = useRef<Map<string, number>>(new Map());
  const rttSamplesRef = useRef<number[]>([]);
  const currentQualityRef = useRef<QualityLevel | null>(null);

  // Post-click input lockout — prevents rage-clicks from queueing ahead
  // of the remote browser. Clamped to measured RTT ∈ [100, 300] ms.
  const clickLockedUntilRef = useRef<number>(0);

  // Navigation-pending watchdog: clear the overlay if no status/frame
  // arrives within 15s (e.g. DNS / proxy stall).
  const navPendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // AbortController for the in-flight POST /record/auth.  Cancels the
  // fetch when the modal closes mid-auth so we don't race a token issue
  // against an abandoned session.
  const abortControllerRef = useRef<AbortController | null>(null);

  // ---------- Session lifecycle ----------

  const startSession = useCallback(
    async (isRetry = false) => {
      // Stay in waiting-for-slot UI on retry to avoid spinner flicker.
      if (!isRetry) {
        setIsStarting(true);
      }
      setStartError(null);

      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        const authRes = await fetch(
          `/api/backend/groups/${groupId}/playbook/record/auth`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            signal: abortController.signal,
          }
        );

        if (!authRes.ok) {
          const body = await authRes.json().catch(() => null);
          throw new Error(body?.detail || `HTTP ${authRes.status}`);
        }

        const { token } = (await authRes.json()) as { token: string };
        if (!token) throw new Error("no token returned");

        if (abortController.signal.aborted) return;

        const ws = new WebSocket(buildWsUrl(groupId, token));
        ws.binaryType = "arraybuffer";
        wsRef.current = ws;

        ws.onmessage = (ev) => {
          if (typeof ev.data === "string") {
            handleMessage(JSON.parse(ev.data) as SessionEvent);
          } else if (ev.data instanceof ArrayBuffer) {
            handleFrame(ev.data);
          } else if (ev.data instanceof Blob) {
            void ev.data.arrayBuffer().then(handleFrame);
          }
        };

        ws.onerror = () => setStartError("WebSocket error");
        ws.onclose = () => {
          // Backstop for closes that don't come with a typed error —
          // surface a retryable message so the user isn't stuck on a
          // spinner.  ``setStartError(prev ?? ...)`` preserves any
          // more-specific error already set by the typed `error` event.
          if (!hasFirstFrameRef.current) {
            setStartError(
              (prev) =>
                prev ??
                "Recording session disconnected before it could start. Please try again."
            );
          }
          wsRef.current = null;
        };

        // Cloud browser is force-disabled in Studio mode — the
        // EU-backend ↔ US-ZenRows relay made interactive recording
        // unusable.  Playbooks still execute cloud-browser at run time.
        const sendStartSession = () => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(
              JSON.stringify({
                cmd: "start_session",
                config: {
                  initial_url: null,
                  ...(browserConfig ?? {}),
                  use_cloud_browser: false,
                },
              })
            );
            // Seed any existing steps so replay works from save 1.
            ws.send(JSON.stringify({ cmd: "set_steps", steps: initialSteps }));
          } else if (ws.readyState === WebSocket.CONNECTING) {
            setTimeout(sendStartSession, 50);
          }
        };
        sendStartSession();
      } catch (e: any) {
        if (
          abortController.signal.aborted ||
          e?.name === "AbortError" ||
          (typeof DOMException !== "undefined" &&
            e instanceof DOMException &&
            e.name === "AbortError")
        ) {
          return;
        }
        setStartError(e?.message || "Failed to start session");
      } finally {
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
        setIsStarting(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialSteps, browserConfig, groupId]
  );

  // Ref-mirror so handleMessage's retry path can invoke startSession
  // without forming a useCallback dependency cycle.
  const startSessionRef = useRef<((isRetry?: boolean) => Promise<void>) | null>(
    null
  );
  useEffect(() => {
    startSessionRef.current = startSession;
  }, [startSession]);

  const closeSession = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (navPendingTimerRef.current) {
      clearTimeout(navPendingTimerRef.current);
      navPendingTimerRef.current = null;
    }
    if (pickTimerRef.current) {
      clearTimeout(pickTimerRef.current);
      pickTimerRef.current = null;
    }
    pendingPickPosRef.current = null;
    setIsWaitingForSlot(false);
    setQueue(null);
    if (wsRef.current) {
      // Detach handlers before close — close is async and onclose would
      // otherwise see the reset hasFirstFrameRef and fire the
      // "disconnected before start" error on every intentional close.
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.onmessage = null;
      try {
        wsRef.current.close();
      } catch {}
      wsRef.current = null;
    }
    if (lastFrameUrlRef.current) {
      URL.revokeObjectURL(lastFrameUrlRef.current);
      lastFrameUrlRef.current = null;
    }
    rttSamplesRef.current = [];
    currentQualityRef.current = null;
    setQuality(null);
    setRipples([]);
    setGhostCursor(null);
    setNavPending(null);
    hasFirstFrameRef.current = false;
    setHasFirstFrame(false);
    setSessionId(null);
    sessionIdRef.current = null;
    setEnvironment(null);
    // No DELETE — closing the WS above triggers WebSocketDisconnect
    // server-side, which runs destroy_session.
  }, []);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  const setRecordStudioOpen = useSetAtom(isRecordStudioOpenAtom);
  useEffect(() => {
    setRecordStudioOpen(open);
    return () => setRecordStudioOpen(false);
  }, [open, setRecordStudioOpen]);

  const followStepTail = useCallback((node: HTMLDivElement | null) => {
    const list = stepListRef.current;
    if (node && list) list.scrollTop = list.scrollHeight;
  }, []);

  useEffect(() => {
    if (open && !sessionId && !isStarting && !isWaitingForSlot) {
      setSteps(initialSteps || []);
      void startSession();
    }
    // Include `isStarting` so Phase-A close (auth POST mid-flight)
    // also tears down — otherwise a session would orphan.
    if (!open && (sessionId || isWaitingForSlot || isStarting || queue)) {
      void closeSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
      if (navPendingTimerRef.current) {
        clearTimeout(navPendingTimerRef.current);
      }
      if (pickTimerRef.current) {
        clearTimeout(pickTimerRef.current);
      }
      if (wsRef.current) {
        // See closeSession for the detach-before-close rationale.
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        wsRef.current.onmessage = null;
        try {
          wsRef.current.close();
        } catch {}
      }
      if (lastFrameUrlRef.current) {
        URL.revokeObjectURL(lastFrameUrlRef.current);
        lastFrameUrlRef.current = null;
      }
    };
  }, []);

  // ---------- WS message dispatch ----------

  const sendCmd = useCallback((cmd: Record<string, unknown>) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(cmd));
    }
  }, []);

  // Feed one RTT sample into the rolling window and maybe nudge the
  // screencast quality preset. Called from pick replies since pick_at is
  // the only command with a server round-trip we already measure.
  const recordRttSample = useCallback(
    (rttMs: number) => {
      const buf = rttSamplesRef.current;
      buf.push(rttMs);
      if (buf.length > 10) buf.shift();
      if (buf.length < 3) return;
      const sorted = [...buf].sort((a, b) => a - b);
      const median = sorted[Math.floor(sorted.length / 2)];
      const level = qualityForRtt(median);
      if (level === currentQualityRef.current) return;
      currentQualityRef.current = level;
      setQuality(level);
      const preset = SCREENCAST_PRESETS[level];
      sendCmd({ cmd: "set_quality", ...preset });
    },
    [sendCmd]
  );

  const handleFrame = useCallback((buf: ArrayBuffer) => {
    const parsed = parseFrameMessage(buf);
    if (!parsed) return;
    const { meta, jpeg } = parsed;
    frameMetaRef.current = {
      device_width: meta.device_width,
      device_height: meta.device_height,
      scroll_x: meta.scroll_x,
      scroll_y: meta.scroll_y,
    };
    // A new frame is our best proxy for "the remote browser has reacted
    // to the last input" — drop optimistic overlays now.
    setRipples((r) => (r.length ? [] : r));
    setNavPending((p) => {
      if (p !== null && navPendingTimerRef.current) {
        clearTimeout(navPendingTimerRef.current);
        navPendingTimerRef.current = null;
      }
      return null;
    });
    hasFirstFrameRef.current = true;
    setHasFirstFrame((h) => (h ? h : true));
    const img = imgRef.current;
    if (img) {
      const prev = lastFrameUrlRef.current;
      const url = URL.createObjectURL(jpeg);
      lastFrameUrlRef.current = url;
      img.src = url;
      // Safe to revoke now — img.src has resolved the blob from the
      // registry synchronously and no longer needs the URL handle.
      if (prev) URL.revokeObjectURL(prev);
    }
  }, []);

  const handleMessage = useCallback(
    (event: SessionEvent) => {
      switch (event.type) {
        case "step": {
          setSteps((prev) => [...prev, event.step]);
          break;
        }
        case "step_update": {
          setSteps((prev) => {
            const next = [...prev];
            if (event.index >= 0 && event.index < next.length) {
              next[event.index] = event.step;
            }
            return next;
          });
          break;
        }
        case "pick": {
          const sentTs = pickSendTsRef.current.get(event.request_id);
          if (sentTs !== undefined) {
            pickSendTsRef.current.delete(event.request_id);
            recordRttSample(performance.now() - sentTs);
          }
          // Ignore stale pick replies — only the most recent request wins.
          if (event.request_id !== latestPickRequestRef.current) break;
          if (!event.bbox) {
            setHoverPick(null);
            break;
          }
          const img = imgRef.current;
          if (!img) break;
          const rect = img.getBoundingClientRect();
          const meta = frameMetaRef.current;
          const scaleX = rect.width / meta.device_width;
          const scaleY = rect.height / meta.device_height;
          setHoverPick({
            bbox: {
              left: event.bbox.x * scaleX,
              top: event.bbox.y * scaleY,
              width: event.bbox.width * scaleX,
              height: event.bbox.height * scaleY,
            },
            label: event.selector?.description || event.selector?.primary || "",
          });
          break;
        }
        case "status": {
          setRecording(event.recording);
          setStatus({ url: event.url, title: event.title });
          if (event.url && event.url !== "about:blank") setUrlBar(event.url);
          // A status update with a fresh URL is the clearest signal that
          // navigation finished — clear the overlay even if no frame has
          // landed yet.
          setNavPending((p) => {
            if (p !== null && event.url && event.url !== "about:blank") {
              if (navPendingTimerRef.current) {
                clearTimeout(navPendingTimerRef.current);
                navPendingTimerRef.current = null;
              }
              return null;
            }
            return p;
          });
          break;
        }
        case "bbox": {
          const resolve = pendingBBoxResolversRef.current.get(event.request_id);
          if (resolve) {
            pendingBBoxResolversRef.current.delete(event.request_id);
            resolve(event.bbox);
          }
          break;
        }
        case "replay": {
          const resolve = pendingReplayResolversRef.current.get(
            event.request_id
          );
          if (resolve) {
            pendingReplayResolversRef.current.delete(event.request_id);
            resolve({ success: event.success, error: event.error });
          }
          break;
        }
        case "queued": {
          setQueue({
            position: event.queue_position,
            depth: event.queue_depth,
          });
          break;
        }
        case "session_created": {
          setIsWaitingForSlot(false);
          setQueue(null);
          setSessionId(event.session_id);
          setEnvironment(event.environment);
          break;
        }
        case "clipboard_data": {
          // Remote→local clipboard sync.  Only write when the backend
          // returned non-null text — null signals "read failed" or "no
          // change since last emit", in which case we silently no-op
          // rather than clobbering the user's local clipboard with an
          // empty string.
          if (event.text && navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(event.text).catch((err: unknown) => {
              console.warn("[recording] clipboard write failed", err);
            });
          }
          break;
        }
        case "error": {
          console.warn("recording error:", event.code, event.message);
          setQueue(null);
          if (event.code === "session_limit_reached") {
            setIsStarting(false);
            setIsWaitingForSlot(true);
            retryTimerRef.current = setTimeout(() => {
              void startSessionRef.current?.(true);
            }, 5000);
          } else if (
            event.code === "invalid_token" ||
            event.code === "start_session_timeout" ||
            event.code === "invalid_first_command" ||
            event.code === "invalid_config" ||
            event.code === "local_protection_forbidden" ||
            event.code === "session_start_failed" ||
            event.code === "browser_disconnected" ||
            event.code === "session_expired" ||
            event.code === "screencast_failed"
          ) {
            setStartError(event.message);
          }
          break;
        }
      }
    },
    [recordRttSample]
  );

  // ---------- Coordinate translation ----------

  const displayToDevice = useCallback((clientX: number, clientY: number) => {
    const img = imgRef.current;
    if (!img) return { x: 0, y: 0 };
    const rect = img.getBoundingClientRect();
    const localX = clientX - rect.left;
    const localY = clientY - rect.top;
    const meta = frameMetaRef.current;
    const scaleX = meta.device_width / rect.width;
    const scaleY = meta.device_height / rect.height;
    return { x: localX * scaleX, y: localY * scaleY };
  }, []);

  // ---------- Canvas interaction ----------

  // Bound for the post-click lockout window (ms). Clamped to the measured
  // RTT so low-latency sessions feel snappy and high-latency sessions
  // don't accept rage-clicks that get queued on the remote browser.
  const currentLockoutMs = useCallback(() => {
    const buf = rttSamplesRef.current;
    if (buf.length === 0) return 100;
    const sorted = [...buf].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    return Math.min(Math.max(median, 100), 300);
  }, []);

  const onCanvasClick = useCallback(
    (e: React.MouseEvent<HTMLImageElement>) => {
      const now = performance.now();
      if (now < clickLockedUntilRef.current) return;
      clickLockedUntilRef.current = now + currentLockoutMs();

      // Display-space coords for the optimistic ripple (rendered in the
      // absolutely-positioned overlay div, which lives on top of <img>).
      const img = imgRef.current;
      if (img) {
        const rect = img.getBoundingClientRect();
        const rx = e.clientX - rect.left;
        const ry = e.clientY - rect.top;
        const id = Math.random().toString(36).slice(2);
        setRipples((r) => [...r, { id, x: rx, y: ry }]);
        setTimeout(() => {
          setRipples((r) => r.filter((p) => p.id !== id));
        }, 500);
      }

      const { x, y } = displayToDevice(e.clientX, e.clientY);
      sendCmd({ cmd: "mouse_click", x, y, button: "left" });
    },
    [currentLockoutMs, displayToDevice, sendCmd]
  );

  // Wheel scrolling is wired via a native (non-passive) listener in the
  // useEffect below.  Three things make this finicky:
  //   1. React 17+ marks synthetic `onWheel` as passive, which makes
  //      `e.preventDefault()` silently no-op — so the host page would
  //      scroll alongside the remote browser.
  //   2. The `<img>` is centred inside a flex wrapper with letterboxing
  //      around it; wheel events over the empty wrapper area never hit
  //      the img.  Attaching to the wrapper means every wheel inside
  //      the studio viewport is captured.
  //   3. The wrapper is rendered CONDITIONALLY on
  //      `!isStarting && !startError && !isWaitingForSlot` — when the
  //      modal first opens, `isStarting` is true and the wrapper isn't
  //      in the DOM yet, so `wrapperRef.current` is null.  We MUST
  //      depend on those gating states so the effect re-runs once the
  //      WS session connects and the wrapper mounts; otherwise the
  //      listener never attaches at all.
  useEffect(() => {
    if (!open) return;
    if (isStarting || startError || isWaitingForSlot) return;
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      // Route the wheel to the element under the cursor on the remote
      // side — CDP `Input.dispatchMouseEvent: mouseWheel` dispatches at
      // whatever (x, y) we pass, so scroll targets the hovered
      // container, not the document root.
      const { x, y } = displayToDevice(e.clientX, e.clientY);
      sendCmd({
        cmd: "scroll",
        delta_x: e.deltaX,
        delta_y: e.deltaY,
        x,
        y,
      });
      // Pointer-relative coords go stale during a scroll — drop any
      // pending pick_at probe so we don't highlight the wrong element.
      if (pickTimerRef.current) {
        clearTimeout(pickTimerRef.current);
        pickTimerRef.current = null;
      }
    };
    wrapper.addEventListener("wheel", onWheel, { passive: false });
    return () => wrapper.removeEventListener("wheel", onWheel);
  }, [
    open,
    isStarting,
    startError,
    isWaitingForSlot,
    displayToDevice,
    sendCmd,
  ]);

  const firePickAt = useCallback(
    (x: number, y: number) => {
      const request_id = Math.random().toString(36).slice(2);
      latestPickRequestRef.current = request_id;
      pickSendTsRef.current.set(request_id, performance.now());
      // Cap the map size so a slow server can't grow it unboundedly.
      if (pickSendTsRef.current.size > 32) {
        const firstKey = pickSendTsRef.current.keys().next().value;
        if (firstKey !== undefined) pickSendTsRef.current.delete(firstKey);
      }
      sendCmd({ cmd: "pick_at", request_id, x, y });
    },
    [sendCmd]
  );

  const onCanvasMouseMove = useCallback(
    (e: React.MouseEvent<HTMLImageElement>) => {
      // Ghost cursor updates on every event (cheap, local-only) — the user
      // sees their own pointer tracked in display coords regardless of
      // network state.
      const img = imgRef.current;
      if (img) {
        const rect = img.getBoundingClientRect();
        setGhostCursor({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      }

      // Debounce pick_at — only fire after the pointer has paused.
      // Every previous queued pick gets cancelled, so a sweep across the
      // viewport sends zero round-trips (vs. 10–15 under the old throttle).
      // 180 ms is short enough to feel instant when you stop, long enough
      // to never fire during continuous motion.
      const { x, y } = displayToDevice(e.clientX, e.clientY);
      pendingPickPosRef.current = { x, y };
      if (pickTimerRef.current) clearTimeout(pickTimerRef.current);
      pickTimerRef.current = setTimeout(() => {
        pickTimerRef.current = null;
        const pos = pendingPickPosRef.current;
        if (pos) firePickAt(pos.x, pos.y);
      }, 180);
    },
    [displayToDevice, firePickAt]
  );

  const onCanvasMouseLeave = useCallback(() => {
    latestPickRequestRef.current = "";
    setHoverPick(null);
    setGhostCursor(null);
    if (pickTimerRef.current) {
      clearTimeout(pickTimerRef.current);
      pickTimerRef.current = null;
    }
    pendingPickPosRef.current = null;
  }, []);

  const onCanvasKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      // ── Clipboard shortcuts (Ctrl+C / Ctrl+V / Ctrl+X) ──
      // We MUST NOT preventDefault on these — preventDefault on the
      // keydown cancels the keydown's default action, which is to
      // fire the subsequent `paste` / `copy` / `cut` DOM event on
      // the focused element.  Cancelling the keydown means our
      // onCanvasPaste / onCanvasCopy / onCanvasCut handlers never
      // receive the event, so paste from the user's local clipboard
      // never reaches the remote browser.  Bail out before the
      // preventDefault call below.
      if (isClipboardShortcut(e)) return;

      // For every other key, suppress the host's default so typing
      // in the studio doesn't also trigger global hotkeys, scroll,
      // focus moves, etc.
      e.preventDefault();
      const cmd = buildKeyCommand({
        key: e.key,
        ctrlKey: e.ctrlKey,
        metaKey: e.metaKey,
        shiftKey: e.shiftKey,
        altKey: e.altKey,
        repeat: e.repeat,
      });
      if (cmd) sendCmd(cmd);
    },
    [sendCmd]
  );

  // ---------- Clipboard bridge ----------
  //
  // Bidirectional sync between the user's local clipboard and the
  // remote browser's clipboard, so Ctrl+C / Ctrl+V "just work" inside
  // the studio.  Copy: forward Ctrl+C as a press combo so the remote
  // page's copy handler runs, then ask the backend to read the remote
  // clipboard and ship it back.  Paste: read the user's local
  // clipboard from the DOM event, ship it to the backend which seeds
  // the remote clipboard then dispatches Ctrl+V.

  const onCanvasCopy = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault();
      sendCmd({ cmd: "press", key: "Control+c" });
      sendCmd({ cmd: "clipboard_read" });
    },
    [sendCmd]
  );

  const onCanvasCut = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault();
      sendCmd({ cmd: "press", key: "Control+x" });
      sendCmd({ cmd: "clipboard_read" });
    },
    [sendCmd]
  );

  const onCanvasPaste = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault();
      const text = e.clipboardData?.getData("text") ?? "";
      // Backend caps at 1 MiB — pre-flight check here keeps the WS
      // message loop from being blocked by accidentally pasted blobs
      // (e.g. an image's base64 representation).
      if (text.length > 1_048_576) {
        console.warn("[recording] paste exceeds 1 MiB cap: sending truncated");
      }
      sendCmd({
        cmd: "clipboard_write_and_paste",
        text: text.slice(0, 1_048_576),
      });
    },
    [sendCmd]
  );

  // ---------- URL bar ----------

  const onSubmitUrl = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      let url = urlBar.trim();
      if (!url) return;
      if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
      // Show a loading veil until the next status/frame arrives. Without
      // this the screencast freezes on the previous page for the entire
      // navigation, which feels broken in cloud mode.
      setNavPending(url);
      if (navPendingTimerRef.current) clearTimeout(navPendingTimerRef.current);
      navPendingTimerRef.current = setTimeout(() => setNavPending(null), 15000);
      sendCmd({ cmd: "navigate", url });
    },
    [sendCmd, urlBar]
  );

  // ---------- Recording toggle ----------

  const toggleRecording = useCallback(() => {
    sendCmd({ cmd: recording ? "stop_recording" : "start_recording" });
  }, [recording, sendCmd]);

  // ---------- Hover-to-highlight (bbox round trip) ----------

  const requestBBox = useCallback(
    (selector: any): Promise<BBoxEvent["bbox"]> => {
      return new Promise((resolve) => {
        const request_id = Math.random().toString(36).slice(2);
        pendingBBoxResolversRef.current.set(request_id, resolve);
        sendCmd({ cmd: "bbox", request_id, selector });
        setTimeout(() => {
          if (pendingBBoxResolversRef.current.has(request_id)) {
            pendingBBoxResolversRef.current.delete(request_id);
            resolve(null);
          }
        }, 1500);
      });
    },
    [sendCmd]
  );

  const [highlight, setHighlight] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);

  // Live hover highlight driven by mousemove over the canvas.
  const [hoverPick, setHoverPick] = useState<{
    bbox: { left: number; top: number; width: number; height: number };
    label: string;
  } | null>(null);

  useEffect(() => {
    if (hoverIndex === null) {
      setHighlight(null);
      return;
    }
    const step = steps[hoverIndex];
    const selector = step?.selector;
    if (!selector) {
      setHighlight(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const bbox = await requestBBox(selector);
      if (cancelled || !bbox) {
        setHighlight(null);
        return;
      }
      const img = imgRef.current;
      if (!img) return;
      const rect = img.getBoundingClientRect();
      const meta = frameMetaRef.current;
      const scaleX = rect.width / meta.device_width;
      const scaleY = rect.height / meta.device_height;
      setHighlight({
        left: bbox.x * scaleX,
        top: bbox.y * scaleY,
        width: bbox.width * scaleX,
        height: bbox.height * scaleY,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [hoverIndex, steps, requestBBox]);

  // ---------- Replay ----------

  const replayStep = useCallback(
    (index: number) => {
      // Push current steps to backend first so indexes line up.
      sendCmd({ cmd: "set_steps", steps });
      const request_id = Math.random().toString(36).slice(2);
      setReplayingIndex(index);
      pendingReplayResolversRef.current.set(request_id, (r) => {
        setReplayingIndex(null);
        if (r.success) {
          setReplayedSteps((prev) => new Set(prev).add(index));
        } else {
          console.warn("replay failed:", r.error);
        }
      });
      sendCmd({ cmd: "replay_step", request_id, index });
    },
    [sendCmd, steps]
  );

  const replayAll = useCallback(async () => {
    sendCmd({ cmd: "set_steps", steps });
    setReplayedSteps(new Set());
    for (let i = 0; i < steps.length; i++) {
      // Bound variables have no value at edit time — stop rather than execute
      // the literal token and every later step against the wrong page state.
      if (stepNotReplayable(steps[i])) break;
      const request_id = Math.random().toString(36).slice(2);
      setReplayingIndex(i);
      const done = new Promise<{ success: boolean; error: string | null }>(
        (resolve) => pendingReplayResolversRef.current.set(request_id, resolve)
      );
      sendCmd({ cmd: "replay_step", request_id, index: i });
      const r = await done;
      if (r.success) {
        setReplayedSteps((prev) => new Set(prev).add(i));
      } else {
        console.warn("replay failed at", i, r.error);
        break;
      }
    }
    setReplayingIndex(null);
  }, [sendCmd, steps]);

  // ---------- Step list management ----------

  const deleteStep = useCallback((index: number) => {
    setSteps((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // ---------- Close handlers ----------
  // Two exit paths with different semantics:
  //   - handleClose  (X corner + "Save & close" button): persists steps to
  //     the node config.
  //   - handleCancel ("Cancel" button): discards steps, leaves the node
  //     config unchanged.

  const handleClose = useCallback(() => {
    onSave(steps);
    onClose();
  }, [onClose, onSave, steps]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // ---------- Layout sync: keep overlay aligned with the image ----------

  useLayoutEffect(() => {
    const img = imgRef.current;
    const overlay = overlayRef.current;
    if (!img || !overlay) return;
    const update = () => {
      const rect = img.getBoundingClientRect();
      const wrap = wrapperRef.current?.getBoundingClientRect();
      if (!wrap) return;
      overlay.style.left = `${rect.left - wrap.left}px`;
      overlay.style.top = `${rect.top - wrap.top}px`;
      overlay.style.width = `${rect.width}px`;
      overlay.style.height = `${rect.height}px`;
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(img);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [sessionId]);

  // ---------- Render ----------

  const showStudio = !isStarting && !startError && !isWaitingForSlot;

  return (
    <BaseDialog
      isOpen={open}
      onOpenChange={(v) => {
        if (!v) handleClose();
      }}
      title="Playbook Studio"
      size="full"
      className="!w-fit !max-w-[90%]"
      disableScroll
      overflowVisible={false}
    >
      {/* Stop keyboard events from reaching the workflow editor (e.g. Backspace deleting the node) */}
      <div
        className="flex flex-col h-full min-h-0"
        onKeyDown={(e) => e.stopPropagation()}
      >
        {isWaitingForSlot && (
          <div className="flex flex-col items-center justify-center py-24 px-12 gap-4 text-center min-w-[480px]">
            <div className="w-10 h-10 flex items-center justify-center bg-accent rounded-xl">
              <Loader2 className="h-5 w-5 animate-spin text-foreground" />
            </div>
            <div className="flex flex-col gap-1.5">
              <p className="text-base font-medium text-foreground">
                Waiting for availability
              </p>
              <p className="text-sm text-muted-foreground max-w-sm">
                All browser sessions are currently in use. You'll be connected
                as soon as one becomes available.
              </p>
            </div>
            <Button variant="outline" onClick={handleCancel} className="mt-2">
              Cancel
            </Button>
          </div>
        )}
        {isStarting && !isWaitingForSlot && (
          <div className="flex flex-col items-center justify-center py-24 px-12 gap-4 text-center min-w-[480px]">
            <div className="w-10 h-10 flex items-center justify-center bg-accent rounded-xl">
              <Loader2 className="h-5 w-5 animate-spin text-foreground" />
            </div>
            <div className="flex flex-col gap-1.5">
              <p className="text-base font-medium text-foreground">
                {queue
                  ? "Waiting for a free sandbox"
                  : "Starting browser session"}
              </p>
              <p className="text-sm text-muted-foreground max-w-sm">
                {queue
                  ? `All execution slots are busy right now. You're in the queue${
                      queue.position
                        ? ` at position ${queue.position}${queue.depth ? ` of ${queue.depth}` : ""}`
                        : ""
                    }; your browser starts automatically as soon as slots free up.`
                  : "Setting up a live browser for you to record interactions."}
              </p>
            </div>
          </div>
        )}
        {startError && !isWaitingForSlot && (
          <div className="flex flex-col items-center justify-center py-24 px-12 gap-4 text-center min-w-[480px]">
            <div className="w-10 h-10 flex items-center justify-center bg-red-50 rounded-xl">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <div className="flex flex-col gap-1.5">
              <p className="text-base font-medium text-foreground">
                {hasFirstFrameRef.current
                  ? "Browser session ended"
                  : "Failed to start session"}
              </p>
              <p className="text-sm text-muted-foreground max-w-sm">
                {startError}
              </p>
            </div>
            <div className="flex gap-2 mt-2">
              <Button variant="outline" onClick={handleCancel}>
                {hasFirstFrameRef.current ? "Discard & close" : "Close"}
              </Button>
              {hasFirstFrameRef.current && (
                <Button onClick={handleClose}>Save & close</Button>
              )}
            </div>
          </div>
        )}
        {showStudio && (
          <div className="flex gap-3 flex-1 min-h-0">
            {/* Left: browser canvas */}
            <div className="flex flex-col min-w-0">
              {environment && (
                <div className="flex gap-1.5 items-center mb-2 text-[11px] text-muted-foreground">
                  <span>Recording as:</span>
                  {environment.cdp && (
                    <span className="px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-700 dark:text-blue-300 font-medium">
                      Cloud Browser
                    </span>
                  )}
                  {environment.proxy && (
                    <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 font-medium">
                      Proxy
                      {browserConfig?.proxy_region &&
                      browserConfig.proxy_region !== "auto"
                        ? ` (${browserConfig.proxy_region})`
                        : ""}
                    </span>
                  )}
                  {environment.stealth && (
                    <span className="px-1.5 py-0.5 rounded bg-warning-500/15 text-warning-700 dark:text-warning-300 font-medium">
                      Stealth
                    </span>
                  )}
                  {!environment.cdp &&
                    !environment.proxy &&
                    !environment.stealth && <span>Local Chromium</span>}
                  {environment.cdp && (
                    <span
                      className={
                        "px-1.5 py-0.5 rounded font-medium " +
                        (quality === "good"
                          ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                          : quality === "laggy"
                            ? "bg-warning-500/15 text-warning-700 dark:text-warning-300"
                            : quality === "degraded"
                              ? "bg-red-500/15 text-red-700 dark:text-red-300"
                              : "bg-muted text-muted-foreground")
                      }
                      title="Measured from pick_at command round-trip time."
                    >
                      {quality === "good"
                        ? "Connection: good"
                        : quality === "laggy"
                          ? "Connection: laggy"
                          : quality === "degraded"
                            ? "Connection: degraded"
                            : "Measuring connection…"}
                    </span>
                  )}
                </div>
              )}
              <form onSubmit={onSubmitUrl} className="flex gap-2 mb-2">
                <Input
                  value={urlBar}
                  onChange={(e) => setUrlBar(e.target.value)}
                  placeholder="https://..."
                  className="flex-1"
                />
                <Button type="submit" variant="outline">
                  Go
                </Button>
                <Button
                  type="button"
                  variant={recording ? "destructive" : "default"}
                  onClick={toggleRecording}
                >
                  {recording ? (
                    <>
                      <StopCircle className="h-4 w-4 mr-1.5" />
                      Stop
                    </>
                  ) : (
                    <>
                      <Circle className="h-4 w-4 mr-1.5" />
                      Record
                    </>
                  )}
                </Button>
              </form>
              <div
                ref={wrapperRef}
                className="relative rounded-md overflow-hidden flex items-center justify-center aspect-video h-[min(65vh,600px)] border focus:outline-none"
                tabIndex={0}
                onMouseDown={() => wrapperRef.current?.focus()}
                onKeyDown={onCanvasKeyDown}
                onCopy={onCanvasCopy}
                onCut={onCanvasCut}
                onPaste={onCanvasPaste}
              >
                <img
                  ref={imgRef}
                  alt=""
                  onClick={onCanvasClick}
                  onMouseMove={onCanvasMouseMove}
                  onMouseLeave={onCanvasMouseLeave}
                  onError={(e) => {
                    console.warn(
                      "[recording] frame image failed to load",
                      (e.target as HTMLImageElement).src?.slice(0, 60)
                    );
                  }}
                  className="max-h-full max-w-full cursor-crosshair select-none"
                  draggable={false}
                />
                <div
                  ref={overlayRef}
                  className="absolute pointer-events-none left-0 top-0"
                >
                  {highlight && (
                    <div
                      className="absolute border-2 border-blue-500 bg-blue-500/20 rounded-sm"
                      style={{
                        left: highlight.left,
                        top: highlight.top,
                        width: highlight.width,
                        height: highlight.height,
                      }}
                    />
                  )}
                  {hoverPick && (
                    <>
                      <div
                        className="absolute border-2 border-emerald-400 bg-emerald-400/10 rounded-sm"
                        style={{
                          left: hoverPick.bbox.left,
                          top: hoverPick.bbox.top,
                          width: hoverPick.bbox.width,
                          height: hoverPick.bbox.height,
                        }}
                      />
                      {hoverPick.label && (
                        <div
                          className="absolute px-1.5 py-0.5 text-[10px] font-mono bg-emerald-500 text-white rounded-sm whitespace-nowrap max-w-[280px] truncate"
                          style={{
                            left: hoverPick.bbox.left,
                            top: Math.max(0, hoverPick.bbox.top - 16),
                          }}
                        >
                          {hoverPick.label}
                        </div>
                      )}
                    </>
                  )}
                  {/* Optimistic click ripple — masks the cloud round-trip
                      by confirming the click before the remote browser
                      actually repaints. */}
                  {ripples.map((r) => (
                    <span
                      key={r.id}
                      className="absolute rounded-full border-2 border-sky-400 bg-sky-400/20 animate-ping"
                      style={{
                        left: r.x - 12,
                        top: r.y - 12,
                        width: 24,
                        height: 24,
                      }}
                    />
                  ))}
                  {/* Ghost cursor — the remote screencast has no pointer,
                      so we render the user's own position locally for
                      instant feedback. */}
                  {ghostCursor && (
                    <span
                      className="absolute rounded-full bg-sky-500/70 ring-2 ring-white/70 shadow-sm"
                      style={{
                        left: ghostCursor.x - 4,
                        top: ghostCursor.y - 4,
                        width: 8,
                        height: 8,
                      }}
                    />
                  )}
                </div>
                {/* First-frame placeholder — avoids the browser's broken-
                    image chrome while the remote screencast spins up. */}
                {!hasFirstFrame && (
                  <div className="absolute inset-0 z-2 flex flex-col gap-2 items-center justify-center bg-muted/30 text-sm pointer-events-none">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    {queue ? (
                      <div className="flex flex-col items-center gap-1 px-8 text-center">
                        <div className="text-sm font-medium text-foreground">
                          Waiting for a free sandbox
                        </div>
                        <div className="max-w-sm text-xs text-muted-foreground">
                          All execution slots are busy right now. You&apos;re in
                          the queue
                          {queue.position
                            ? ` at position ${queue.position}${queue.depth ? ` of ${queue.depth}` : ""}`
                            : ""}
                          ; your browser starts automatically as soon as slots
                          free up.
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs text-muted-foreground">
                        Waiting for first frame…
                      </div>
                    )}
                  </div>
                )}
                {/* Navigation / action loading veil — covers the stale
                    previous-page frame while the cloud browser navigates.
                    pointer-events-auto so it also swallows clicks that
                    would otherwise queue up on the remote browser. */}
                {navPending && (
                  <div className="absolute inset-0 z-3 flex flex-col gap-2 items-center justify-center bg-background/70 backdrop-blur-sm text-sm">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    <div className="text-xs text-muted-foreground max-w-[80%] truncate">
                      Loading {navPending}
                    </div>
                  </div>
                )}
                <canvas ref={canvasRef} className="hidden" />
                {(status.url || status.title) && (
                  <div className="absolute bottom-0 left-0 right-0 px-2 py-1 bg-background/80 backdrop-blur-sm border-t text-[11px] text-muted-foreground truncate z-2">
                    {status.title ? `${status.title}: ` : ""}
                    {status.url}
                  </div>
                )}
              </div>
            </div>

            {/* Right: steps sidebar — stretch to match the browser panel */}
            <div className="w-[360px] self-stretch min-h-0 max-h-[calc(min(65vh,600px)_+_4rem)] flex flex-col border rounded-md overflow-hidden">
              <div className="shrink-0 px-3 py-2 border-b flex items-center justify-between">
                <div className="font-medium text-sm">
                  Steps ({steps.length})
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={replayAll}
                  disabled={steps.length === 0 || replayingIndex !== null}
                >
                  <Play className="h-3.5 w-3.5 mr-1" />
                  Replay all
                </Button>
              </div>
              {steps.some(stepNotReplayable) && (
                <p className="shrink-0 px-3 py-1.5 text-[11px] leading-snug text-muted-foreground border-b bg-muted/30">
                  Some steps can't be replayed here. Bound-variable values only
                  exist when the workflow runs, and script steps run only with
                  the workflow.
                </p>
              )}
              <div
                ref={stepListRef}
                className="flex-1 min-h-0 overflow-y-auto thin-scrollbars p-2 space-y-1"
              >
                {steps.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-full gap-4 text-center px-6">
                    <MousePointer
                      className="h-8 w-8 text-foreground"
                      strokeWidth={1}
                    />
                    <p className="text-base font-medium text-foreground leading-none">
                      No steps yet
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed max-w-[200px] text-balance">
                      Click <b>Record</b> and interact with the page to capture
                      steps.
                    </p>
                  </div>
                )}
                {steps.map((step, i) => (
                  <div
                    key={`${step.id || i}`}
                    ref={i === steps.length - 1 ? followStepTail : undefined}
                    className={
                      "group flex items-center gap-2 px-2 py-1.5 rounded text-xs border " +
                      (hoverIndex === i
                        ? "bg-blue-50 border-blue-300"
                        : "bg-background border-transparent hover:border-border")
                    }
                    onMouseEnter={() => setHoverIndex(i)}
                    onMouseLeave={() => setHoverIndex(null)}
                  >
                    <span className="tabular-nums w-5 text-right shrink-0">
                      {replayedSteps.has(i) ? (
                        <Check className="h-3.5 w-3.5 text-emerald-500 inline-block" />
                      ) : (
                        <span className="text-muted-foreground">{i + 1}</span>
                      )}
                    </span>
                    <MousePointer className="h-3 w-3 shrink-0 text-muted-foreground" />
                    <span className="flex-1 truncate">
                      {stepLabel(step, formatStepValue)}
                    </span>
                    <span
                      title={
                        stepNotReplayable(step)
                          ? replayUnavailableTitle(step)
                          : "Replay this step"
                      }
                    >
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6 opacity-0 group-hover:opacity-100"
                        onClick={() => replayStep(i)}
                        disabled={
                          replayingIndex !== null || stepNotReplayable(step)
                        }
                      >
                        {replayingIndex === i ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Play className="h-3 w-3" />
                        )}
                      </Button>
                    </span>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 opacity-0 group-hover:opacity-100"
                      onClick={() => deleteStep(i)}
                      title="Delete"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="shrink-0 p-2 border-t flex justify-end gap-2">
                <Button variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button onClick={handleClose}>Save & close</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </BaseDialog>
  );
}
